/* eslint-disable */
import "./List.scss";
import * as React from "react";
import AutoSizer from "react-virtualized/dist/commonjs/AutoSizer";
import VirtualizedList from "react-virtualized/dist/commonjs/List";
import CheckBox from "@mui/material/Checkbox";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import ArrowDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowUpIcon from "@mui/icons-material/ArrowDropUp";
import ListActions from "./ListActions";
import * as ListInterface from "./ListInterface";
import classNames from "../../helpers/classNames";

interface State<T> {
  activeColumn: string;
  activeRow: string | number;
  shouldSort: boolean;
  sortOrder: number; // -1 | 1
  processedData: Array<T | ListInterface.SegmentRowType<T>>;
  originalData: ListInterface.DataType[];
  selectedRows: { [key: string]: boolean };
  testmationLabel?: string;
  maxAvailableHeight: number;
}

export const EmptyView = (props) => (
  <div style={{ height: "100%", width: "100%" }}>{props.children}</div>
);

export const LoadingView = (props) => (
  <div style={{ height: "100%", width: "100%" }}>{props.children}</div>
);

export default class List<T extends ListInterface.DataType> extends React.Component<
  ListInterface.PropsType<T>,
  State<T>
> {
  public static sortFunction = (key) => (a, b) => a[key] > b[key] ? 1 : a[key] === b[key] ? 0 : -1;

  public static sortData = (data, sortOrder, sortFunction) =>
    data.sort((a, b) => sortOrder * sortFunction(a, b, sortOrder));

  /**
   * Inserts Segment row data to the data array.
   *
   * @static
   * @memberof List
   */
  public static segmentize<U>(rowData, segmentBy) {
    const segmentedData = [];
    let currentSegment: ListInterface.SegmentRowType<U>;
    rowData.forEach((datum) => {
      const segment = segmentBy(datum);
      if (!currentSegment || currentSegment.segment !== segment) {
        currentSegment = {
          id: btoa(unescape(encodeURIComponent(segment))),
          segment,
          rows: [],
          __meta__row_type: "segment_summary"
        };
        segmentedData.push(currentSegment);
      }
      currentSegment.rows.push(datum);
      segmentedData.push(datum);
    });
    return segmentedData;
  }

  private containerRef = React.createRef<HTMLDivElement>();
  private resizeTimeout: number | null = null;

  constructor(props: ListInterface.PropsType<T>) {
    super(props);
    const initialMaxHeight =
      typeof window !== "undefined" ? Math.max(200, window.innerHeight - 250) : 600;
    this.state = {
      activeColumn: props.defaultSortColumn,
      activeRow: props.defaultActiveRow,
      shouldSort: props.shouldSort !== undefined ? props.shouldSort : true,
      sortOrder: props.defaultSortOrder || 1,
      originalData: props.data || [],
      processedData: props.data || [],
      selectedRows: {},
      maxAvailableHeight: initialMaxHeight
    };
  }

  public componentDidMount() {
    this.updateMaxAvailableHeight();
    if (typeof window !== "undefined") {
      window.addEventListener("resize", this.handleResize);
    }
    const activeColumnConfig = this.columnConfig();
    if (activeColumnConfig && activeColumnConfig.sortable) {
      this.setState({ processedData: this.processData() });
    }
  }

  public componentWillUnmount() {
    if (typeof window !== "undefined") {
      window.removeEventListener("resize", this.handleResize);
    }
    if (this.resizeTimeout) {
      window.cancelAnimationFrame(this.resizeTimeout);
    }
  }

  public UNSAFE_componentWillReceiveProps(nextProps: ListInterface.PropsType<T>) {
    const dataChanged = this.props.data !== nextProps.data;
    const shouldSortChanged = this.props.shouldSort !== nextProps.shouldSort;
    const sortColumnChanged =
      Boolean(nextProps.defaultSortColumn) &&
      nextProps.defaultSortColumn !== this.props.defaultSortColumn;

    if (dataChanged || shouldSortChanged || sortColumnChanged) {
      const activeColumn = sortColumnChanged
        ? nextProps.defaultSortColumn!
        : this.state.activeColumn;
      const stateUpdate: Partial<State<T>> = {
        originalData: nextProps.data,
        activeColumn,
        processedData: this.processData(
          nextProps.data,
          this.state.sortOrder,
          activeColumn,
          nextProps.shouldSort
        )
      };
      if (this.props.multiSelectable && dataChanged) {
        const trimmed = this.trimSelectedRows(nextProps.data);
        stateUpdate.selectedRows = trimmed;
        if (
          this.props.onMultiSelect &&
          Object.keys(trimmed).length !== Object.keys(this.state.selectedRows).length
        ) {
          this.props.onMultiSelect(Object.keys(trimmed).map((id) => Number(id)));
        }
      }
      this.setState(stateUpdate as State<T>, () => {
        this.updateMaxAvailableHeight();
      });
    }
  }

  private getAvailableHeight(): number {
    if (!this.containerRef.current || typeof window === "undefined") {
      return 600;
    }
    const container = this.containerRef.current;
    const rect = container.getBoundingClientRect();
    const headerEl = container.querySelector(".okhati-list-header");
    const headerHeight = headerEl ? headerEl.clientHeight : this.props.hideHeader ? 0 : 40;
    const topOffset = Math.max(0, rect.top);

    // Check if there is an ancestor with constrained height (dialog or fixed-height scroll container)
    let maxBottom = window.innerHeight;
    let bottomSpacing = 0;
    let el = container.parentElement;
    while (el && el !== document.body && el !== document.documentElement) {
      const style = window.getComputedStyle(el);
      bottomSpacing += parseFloat(style.paddingBottom) || 0;

      const role = el.getAttribute("role");
      const isDialog = role === "dialog" || el.classList.contains("MuiDialog-paper");
      if (isDialog) {
        maxBottom = Math.min(maxBottom, el.getBoundingClientRect().bottom - bottomSpacing);
        break;
      }
      const isScrollable =
        style.overflowY === "auto" ||
        style.overflowY === "scroll" ||
        style.overflow === "auto" ||
        style.overflow === "scroll";
      if (isScrollable && el.clientHeight > 0 && el.clientHeight < window.innerHeight) {
        const parentRect = el.getBoundingClientRect();
        const parentEl = el.parentElement;
        const parentStyle = parentEl ? window.getComputedStyle(parentEl) : null;
        const isInFlexColumn =
          Boolean(parentStyle) &&
          parentStyle!.display.includes("flex") &&
          parentStyle!.flexDirection.includes("column");
        const hasExplicitHeight =
          (Boolean(el.style.height) && el.style.height.endsWith("px")) ||
          (Boolean(el.style.maxHeight) && el.style.maxHeight.endsWith("px")) ||
          (Boolean(style.maxHeight) && style.maxHeight !== "none");
        const isFlexContentSized =
          isInFlexColumn &&
          !hasExplicitHeight &&
          (style.flexGrow === "0" || !style.flexGrow) &&
          (style.flexBasis === "auto" || !style.flexBasis);
        const clientBottom = parentRect.top + el.clientTop + el.clientHeight;
        const isHuggingContent =
          !hasExplicitHeight && Math.abs(clientBottom - bottomSpacing - rect.bottom) <= 5;

        if (!isFlexContentSized && !isHuggingContent && clientBottom - bottomSpacing > topOffset) {
          maxBottom = Math.min(maxBottom, clientBottom - bottomSpacing);
          break;
        }
      }

      bottomSpacing += parseFloat(style.marginBottom) || 0;
      el = el.parentElement;
    }

    maxBottom = Math.min(maxBottom, window.innerHeight - bottomSpacing);
    const available = maxBottom - topOffset - headerHeight - 20;
    return Math.max(150, Math.floor(available));
  }

  private updateMaxAvailableHeight = () => {
    const maxAvailableHeight = this.getAvailableHeight();
    if (Math.abs(this.state.maxAvailableHeight - maxAvailableHeight) > 10) {
      this.setState({ maxAvailableHeight });
    }
  };

  private handleResize = () => {
    if (this.resizeTimeout) {
      window.cancelAnimationFrame(this.resizeTimeout);
    }
    this.resizeTimeout = window.requestAnimationFrame(() => {
      this.updateMaxAvailableHeight();
    });
  };

  private trimSelectedRows(newData: T[]) {
    if (!newData || !Object.keys(this.state.selectedRows).length) {
      return {};
    }
    const newIdMap = new Set(newData.map((el) => String(el.id)));
    const selectedRows: { [key: string]: boolean } = {};
    for (const id in this.state.selectedRows) {
      if (this.state.selectedRows[id] && newIdMap.has(String(id))) {
        selectedRows[id] = true;
      }
    }
    return selectedRows;
  }

  private onColumnHeaderClick(column) {
    const sortOrder = this.state.activeColumn === column ? this.state.sortOrder * -1 : 1;
    this.setState({
      activeColumn: column,
      sortOrder,
      processedData: this.processData(this.props.data, sortOrder, column)
    });
    const colCfg = this.columnConfig(column);
    if (colCfg && colCfg.onCustomSort) {
      colCfg.onCustomSort();
    }
  }

  private onRowClick(row, excludeRowClick = false) {
    if (excludeRowClick) {
      return;
    }
    if (this.props.activeRow === undefined) {
      this.setState({ activeRow: row.id });
    }
    if (this.isAtleastOneSelected() && !this.state.selectedRows[row.id]) {
      this.clearAllSelected();
    }

    if (this.props.onRowClick) {
      this.props.onRowClick(row);
    }
  }

  private columnConfig(activeColumn = this.state.activeColumn) {
    return this.props.columns?.find((c) => c.key === activeColumn);
  }

  private processData(
    data = this.props.data,
    order = this.state.sortOrder,
    column = this.state.activeColumn,
    shouldSortProp = this.props.shouldSort
  ) {
    if (!data || data.length === 0) {
      return [];
    }
    const columnConfig = this.columnConfig(column);
    if (!columnConfig) {
      return [...data];
    }
    const { sortable, segmentable, sortFunction } = columnConfig;
    let processedData = [...data];
    const shouldSort = shouldSortProp !== undefined ? shouldSortProp : true;
    if (sortable && shouldSort) {
      processedData = List.sortData(
        processedData,
        order,
        sortFunction || List.sortFunction(column)
      );
    }
    if (segmentable) {
      processedData = this.segmentizeData(processedData, column);
    }
    return processedData;
  }

  private segmentizeData(data, column = this.state.activeColumn) {
    const columnCfg = this.columnConfig(column);
    if (!columnCfg) return data;
    const { segmentable, segmentBy } = columnCfg;
    let segmentedData = data;
    if (segmentable && segmentBy) {
      segmentedData = List.segmentize(data, segmentBy);
    }
    return segmentedData;
  }

  private getChildComponent(componentType) {
    return (
      React.Children.toArray(this.props.children).find(
        (child) => React.isValidElement(child) && child.type === componentType
      ) || null
    );
  }

  private getActiveRow() {
    return this.props.activeRow ? this.props.activeRow : this.state.activeRow;
  }

  private selectRowToggle(id) {
    const selectedRows = { ...this.state.selectedRows };
    selectedRows[id] = !selectedRows[id];
    if (!selectedRows[id]) {
      delete selectedRows[id];
    }
    this.setState({ selectedRows });
    if (this.props.onMultiSelect) {
      this.props.onMultiSelect(
        Object.keys(selectedRows)
          .filter((id) => selectedRows[id])
          .map((id) => Number(id))
      );
    }
  }

  private selectAllRowsToggle() {
    let selectedRows = {};
    if (this.areAllRowsSelected()) {
      selectedRows = {};
    } else {
      selectedRows = (this.props.data || []).reduce((acc, d) => {
        acc[d.id] = true;
        return acc;
      }, {});
    }
    this.setState({ selectedRows }, () => {
      if (this.props.onMultiSelect) {
        this.props.onMultiSelect(Object.keys(selectedRows).map((id) => Number(id)));
      }
    });
  }

  private clearAllSelected() {
    this.setState({ selectedRows: {} });
    if (this.props.onMultiSelect) {
      this.props.onMultiSelect([]);
    }
  }

  private areAllRowsSelected() {
    const data = this.props.data || [];
    if (data.length === 0) return false;
    return data.every((d) => Boolean(this.state.selectedRows[d.id]));
  }

  private isMultiSelected() {
    for (const k in this.state.selectedRows) {
      if (this.state.selectedRows[k]) return true;
    }
    return false;
  }

  private isAtleastOneSelected() {
    return this.isMultiSelected();
  }

  private renderColumnHeaders() {
    const headers = [];
    if (this.props.multiSelectable) {
      headers.push(
        <div key="selectHeader" className="okhati-list-headercell">
          <CheckBox
            checked={this.areAllRowsSelected()}
            onChange={(e) => {
              this.selectAllRowsToggle();
            }}
          />
        </div>
      );
    }
    if (
      this.props.multiSelectable &&
      this.isMultiSelected() &&
      this.props.multiSelectContextHeader
    ) {
      headers.push(this.props.multiSelectContextHeader);
    } else {
      this.props.columns?.forEach((c, index) => {
        let caret;
        const classes = `okhati-list-headercell headercell-${c.key}`;
        if (this.state.activeColumn === c.key && this.columnConfig(c.key)?.sortable) {
          caret = this.state.sortOrder === 1 ? <ArrowUpIcon /> : <ArrowDownIcon />;
        }
        headers.push(
          <div
            key={c.key}
            className={classNames(classes, {
              hideInNarrowView: this.props.isResponsive && c.hideInNarrowView,
              alwaysHidden: c.alwaysHidden
            })}
            data-automation={`${this.props.automation}-column-header-${c.key}`}
            onClick={() => this.onColumnHeaderClick(c.key)}
            style={{
              paddingLeft:
                this.props.columns[0]?.hideInNarrowView && index == 1 ? "20px" : undefined
            }}
          >
            <Typography variant="caption">
              <Box component="span" fontSize="0.8rem" fontWeight={500}>
                {c.label || c.key}
              </Box>
            </Typography>
            {caret}
          </div>
        );
      });
    }
    if (this.getChildComponent(ListActions)) {
      headers.push(
        <div key="okhati-list-actions" className="okhati-list-headercell actions">
          {this.renderListActions()}
        </div>
      );
    }
    return headers;
  }

  private renderSegment(datum, datumKey, style) {
    const activeCfg = this.columnConfig();
    const key = activeCfg?.key || "";
    return (
      <div key={datumKey} className="okhati-list-row okhati-list-segment-sumary" style={style}>
        {this.props.segementSummaryRenderer
          ? this.props.segementSummaryRenderer(datum, key)
          : datum.segment}
      </div>
    );
  }

  private getRowCells(datum) {
    let rowCells = (this.props.columns || []).map((c, index) => {
      const colCfg = this.columnConfig(c.key) || c;
      const { formatter, cellRenderer, hideInNarrowView } = colCfg;
      const classes = `okhati-list-rowcell rowcell-${c.key}`;
      return (
        <div
          key={c.key}
          className={classNames(classes, {
            hideInNarrowView: this.props.isResponsive && hideInNarrowView,
            alwaysHidden: c.alwaysHidden
          })}
          data-automation={`${this.props.automation}-column-cell-${c.key}`}
          onClick={() => this.onRowClick(datum, c.excludeRowClick)}
          style={{
            paddingLeft: this.props.columns[0]?.hideInNarrowView && index == 1 ? "20px" : undefined,
            overflow: "hidden"
          }}
        >
          {cellRenderer
            ? cellRenderer(datum)
            : formatter
            ? formatter(datum)
            : <Typography variant="body2">{datum[c.key]}</Typography> || ""}
        </div>
      );
    });
    if (this.props.multiSelectable) {
      const rowSelectCell = (
        <div
          key="selectorCell"
          className="okhati-list-rowcell"
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <CheckBox
            checked={Boolean(this.state.selectedRows[datum.id])}
            onChange={(e) => {
              this.selectRowToggle(datum.id);
              e.stopPropagation();
            }}
          />
        </div>
      );
      rowCells = [rowSelectCell].concat(rowCells);
    }
    return rowCells;
  }

  private renderRow = ({
    index,
    isScrolling,
    key,
    style
  }: {
    index: number;
    isScrolling?: boolean;
    key: string;
    style: React.CSSProperties;
  }) => {
    const datum = this.state.processedData[index];
    if (!datum) return null;
    const isMultiSelected = Boolean(this.state.selectedRows[datum.id]);
    if (datum.__meta__row_type === "segment_summary") {
      return this.renderSegment(datum, key, style);
    }
    if (this.props.rowRenderer) {
      return this.props.rowRenderer(datum as T, key, style);
    }
    const className = `okhati-list-row ${
      this.getActiveRow() === datum.id || isMultiSelected ? "active" : ""
    }`;

    return (
      <div
        key={key}
        className={classNames(className, datum.__meta__classname)}
        style={{
          ...style,
          ...(this.props.errorRows?.length
            ? {
                backgroundColor: this.props.errorRows?.includes(index) ? "#ff6961" : "#fafafa"
              }
            : {})
        }}
        data-automation={`${this.props.automation}-row-${datum.id}`}
        onClick={() => this.onRowClick(datum)}
      >
        {this.getRowCells(datum)}
      </div>
    );
  };

  public renderListActions() {
    const listActions = this.getChildComponent(ListActions);
    if (listActions && React.isValidElement(listActions)) {
      return (
        <ListActions
          {...(listActions.props as any)}
          getProcessedData={() => this.state.processedData}
        />
      );
    }
    return null;
  }

  public render() {
    let rowsStyle: React.CSSProperties = {};
    if (this.props.adjustHeightToContents) {
      const contentHeight = (this.props.rowHeight || 50) * this.state.processedData.length + 10;
      const maxHeight = this.props.listHeight || this.state.maxAvailableHeight || 600;
      const height = Math.min(contentHeight, maxHeight);
      rowsStyle = { height };
    } else if (this.props.listHeight) {
      rowsStyle = { height: this.props.listHeight };
    }

    return (
      <div
        ref={this.containerRef}
        className="okhati-list-container"
        data-automation={this.props.automation}
      >
        {!this.props.hideHeader && (
          <div className="okhati-list-header">{this.renderColumnHeaders()}</div>
        )}
        {this.props.isLoading && this.getChildComponent(LoadingView)}
        {!this.props.isLoading &&
          this.state.processedData.length === 0 &&
          this.getChildComponent(EmptyView)}
        {!this.props.isLoading && this.state.processedData.length > 0 && (
          <div className="okhati-list-rows" style={rowsStyle}>
            <AutoSizer
              sortState={this.state.sortOrder}
              activeColumn={this.state.activeColumn}
              selectedRows={this.state.selectedRows}
              activeRow={this.getActiveRow()}
            >
              {({ height, width }) => {
                const effectiveHeight =
                  this.props.listHeight ||
                  (rowsStyle.height as number) ||
                  (height > 0 ? height : this.state.maxAvailableHeight || 500);
                const effectiveWidth = this.props.listWidth || (width > 0 ? width : 800);
                return (
                  <VirtualizedList
                    height={effectiveHeight}
                    width={effectiveWidth}
                    rowCount={this.state.processedData.length}
                    rowHeight={this.props.rowHeight || 50}
                    rowRenderer={this.renderRow}
                    sortState={this.state.sortOrder + this.state.activeColumn}
                    selectedRows={this.state.selectedRows}
                    activeRow={this.getActiveRow()}
                    overscanRowCount={10}
                  />
                );
              }}
            </AutoSizer>
          </div>
        )}
      </div>
    );
  }
}
